import RichText from "@/components/RichText";
import SmartImage from "@/components/SmartImage";
import { resolveButtonStyle } from "@/lib/ctaButtonStyle";
import CtaButtonBlocks, { ctaButtonBlocks } from "./CtaButtonBlocks";

// Modèle de bandeau principal « image à côté ». Volontairement beaucoup plus
// simple que HeroSection.jsx (le hero historique, avec sa pastille, sa note
// et son formulaire de rappel) : c'est cette sobriété qui en fait un style
// DIFFÉRENT, pas une variante de plus du même écran. Mêmes noms de champs
// que lui, pour qu'un texte rédigé pour l'un reste valable pour l'autre.
const DEFAULTS = {
  eyebrow: "",
  headline: "Votre accroche principale",
  subheadline: "",
  cta_primary_label: "Nous contacter",
  cta_primary_url: "/#contact",
  cta_secondary_label: "",
  cta_secondary_url: "",
  image_url: "",
  image_position: "right",
  variant: "light",
  anchor_id: "",
};

export default function HeroSplitSection({ props = {}, blocks }) {
  const t = { ...DEFAULTS, ...props };
  const primary = resolveButtonStyle(t, "cta_primary");
  const secondary = resolveButtonStyle(t, "cta_secondary");
  const extraButtons = ctaButtonBlocks(blocks);
  const dark = t.variant === "dark";

  return (
    <section id={t.anchor_id || undefined} className={`${dark ? "bg-ink" : "bg-cream"} scroll-mt-28`}>
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-14 md:py-20 grid gap-10 md:grid-cols-2 md:items-center">
        {/* L'ordre visuel suit `image_position` sans changer l'ordre du DOM :
            le texte reste lu en premier par un lecteur d'écran, quel que
            soit le côté choisi pour l'image. */}
        <div className={t.image_position === "left" ? "md:order-2" : ""}>
          {t.eyebrow && (
            <RichText as="p" className="text-[13px] font-semibold tracking-wide text-gold uppercase mb-3" html={t.eyebrow} />
          )}
          <RichText
            as="h1"
            className={`font-display font-semibold text-[34px] md:text-[46px] leading-[1.08] tracking-[-0.02em] ${dark ? "text-white" : "text-ink"}`}
            html={t.headline}
          />
          {t.subheadline && (
            <RichText
              as="p"
              preserveParagraphs
              className={`text-[16px] leading-relaxed mt-5 max-w-lg ${dark ? "text-white/75" : "text-graphite"}`}
              html={t.subheadline}
            />
          )}
          {(t.cta_primary_label || t.cta_secondary_label || extraButtons.length > 0) && (
            <div className="flex flex-wrap items-center gap-3 mt-8">
              {t.cta_primary_label && (
                <a
                  href={t.cta_primary_url || "#"}
                  className={`inline-flex items-center gap-2 px-7 py-3 text-[14px] font-semibold hover:opacity-90 transition-opacity ${
                    dark ? "bg-white text-ink" : "bg-ink text-white"
                  } ${primary.className}`}
                  style={primary.style}
                  aria-label={!primary.showText ? t.cta_primary_label : undefined}
                >
                  {primary.showIcon && primary.Icon && <primary.Icon className="h-4 w-4" />}
                  {primary.showText && t.cta_primary_label}
                </a>
              )}
              {t.cta_secondary_label && (
                <a
                  href={t.cta_secondary_url || "#"}
                  className={`inline-flex items-center gap-2 border-control px-7 py-3 text-[14px] font-semibold transition-colors ${
                    dark ? "border-white/25 text-white hover:bg-white/10" : "border-stone text-ink hover:bg-ink/5"
                  } ${secondary.className}`}
                  style={secondary.style}
                  aria-label={!secondary.showText ? t.cta_secondary_label : undefined}
                >
                  {secondary.showIcon && secondary.Icon && <secondary.Icon className="h-4 w-4" />}
                  {secondary.showText && t.cta_secondary_label}
                </a>
              )}
              <CtaButtonBlocks blocks={extraButtons} tone={dark ? "dark" : "light"} />
            </div>
          )}
        </div>

        {/* Sans image, la colonne disparaît plutôt que de laisser un vide :
            le texte reprend alors toute la largeur. */}
        {t.image_url && (
          <div className={t.image_position === "left" ? "md:order-1" : ""}>
            {/* `priority` : l'image d'un bandeau d'ouverture est l'image LCP
                de la page — la différer dégrade la métrique que Google
                mesure en premier. */}
            <SmartImage
              src={t.image_url}
              alt=""
              priority
              className="w-full aspect-[4/3] object-cover rounded-2xl"
            />
          </div>
        )}
      </div>
    </section>
  );
}
